import React, { useCallback, useEffect, useMemo, useState, startTransition } from "react";
import { createRoot } from "react-dom/client";
import {
  Activity,
  AlertTriangle,
  Bell,
  Columns3,
  Download,
  Eye,
  Filter,
  Gauge,
  History,
  Layers,
  Lock,
  RefreshCcw,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  TableProperties,
  WalletCards
} from "lucide-react";
import "./styles.css";

declare global {
  interface Window {
    shopify?: {
      idToken?: () => Promise<string>;
    };
  }
}

type ApiState<T> = { loading: boolean; data?: T; error?: string };
type Page =
  | "Dashboard"
  | "Activity Timeline"
  | "Advanced Filters"
  | "Saved Filters"
  | "Diff Explorer"
  | "Risk Center"
  | "Alerts"
  | "Exports"
  | "Coverage Matrix"
  | "Sources & Sync"
  | "Retention & Usage"
  | "Settings"
  | "Billing"
  | "Logs";

const pages: Array<{ name: Page; icon: React.ComponentType<{ size?: number }> }> = [
  { name: "Dashboard", icon: Gauge },
  { name: "Activity Timeline", icon: Activity },
  { name: "Advanced Filters", icon: Filter },
  { name: "Saved Filters", icon: Layers },
  { name: "Diff Explorer", icon: Columns3 },
  { name: "Risk Center", icon: AlertTriangle },
  { name: "Alerts", icon: Bell },
  { name: "Exports", icon: Download },
  { name: "Coverage Matrix", icon: ShieldCheck },
  { name: "Sources & Sync", icon: RefreshCcw },
  { name: "Retention & Usage", icon: History },
  { name: "Settings", icon: Settings },
  { name: "Billing", icon: WalletCards },
  { name: "Logs", icon: TableProperties }
];

async function authHeaders(): Promise<HeadersInit> {
  if (!window.shopify?.idToken) return {};
  const token = await window.shopify.idToken();
  return { authorization: `Bearer ${token}` };
}

function hasShopifyLaunchParams(): boolean {
  const params = new URLSearchParams(window.location.search);
  return Boolean(params.get("shop"));
}

async function waitForShopifyToken(timeoutMs = 2500): Promise<boolean> {
  if (window.shopify?.idToken) return true;
  if (!hasShopifyLaunchParams()) return false;
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    await new Promise((resolve) => window.setTimeout(resolve, 50));
    if (window.shopify?.idToken) return true;
  }
  return Boolean(window.shopify?.idToken);
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  const tokenHeaders = await authHeaders();
  for (const [key, value] of Object.entries(tokenHeaders)) headers.set(key, value);
  const response = await fetch(path, { ...init, headers, credentials: "omit" });
  const json = (await response.json()) as T & { error?: string };
  if (!response.ok) throw new Error(json.error || `Request failed: ${response.status}`);
  return json;
}

function App() {
  const [page, setPage] = useState<Page>("Dashboard");
  const [session, setSession] = useState<ApiState<Record<string, unknown>>>({ loading: true });
  const [dashboard, setDashboard] = useState<ApiState<Record<string, any>>>({ loading: true });
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setSession({ loading: true });
    setDashboard({ loading: true });
    try {
      const hasToken = await waitForShopifyToken();
      if (!hasToken) {
        setSession({ loading: false, error: "Open LogFilters Guard from Shopify Admin or the Shopify App Store listing to continue installation." });
        setDashboard({ loading: false });
        return;
      }
      const sessionData = await api<Record<string, unknown>>("/api/session");
      setSession({ loading: false, data: sessionData });
      await api("/api/events", { method: "POST", body: JSON.stringify({ signal: "app.loaded" }) });
      const dashboardData = await api<Record<string, any>>("/api/dashboard");
      setDashboard({ loading: false, data: dashboardData });
    } catch (error) {
      setSession({ loading: false, error: error instanceof Error ? error.message : "Session failed" });
      setDashboard({ loading: false, error: "Dashboard requires an embedded Shopify session." });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onNavigate = (name: Page) => {
    startTransition(() => setPage(name));
  };

  const data = dashboard.data || {};

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">LF</div>
          <div>
            <strong>LogFilters Guard</strong>
            <span>Change monitor</span>
          </div>
        </div>
        <nav aria-label="LogFilters Guard navigation">
          {pages.map(({ name, icon: Icon }) => (
            <button key={name} className={page === name ? "active" : ""} onClick={() => onNavigate(name)}>
              <Icon size={17} />
              <span>{name}</span>
            </button>
          ))}
        </nav>
      </aside>
      <main className="workspace">
        <header className="topbar">
          <div>
            <p className="subtle">Audit critical store changes with filters, diffs, and alerts.</p>
            <h1>{page}</h1>
          </div>
          <div className="topbar-actions">
            <label className="searchbox">
              <Search size={16} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search events, SKU, order, actor" />
            </label>
            <button className="icon-button" onClick={() => void load()} title="Refresh">
              <RefreshCcw size={18} />
            </button>
          </div>
        </header>
        {session.error ? <InstallState message={session.error} /> : null}
        {!session.error && <PageView page={page} dashboard={data} query={query} loading={dashboard.loading} />}
      </main>
    </div>
  );
}

function InstallState({ message }: { message: string }) {
  const params = new URLSearchParams(window.location.search);
  const shop = params.get("shop") || "";
  const startInstall = () => {
    if (!shop.trim()) return;
    window.location.href = `/api/auth/shopify/start?shop=${encodeURIComponent(shop.trim())}`;
  };
  return (
    <section className="empty-state">
      <Lock size={28} />
      <h2>Open inside Shopify admin</h2>
      <p>{message}</p>
      {shop ? (
        <div className="inline-form">
          <button onClick={startInstall}>Continue secure installation</button>
        </div>
      ) : null}
    </section>
  );
}

function PageView({ page, dashboard, query, loading }: { page: Page; dashboard: Record<string, any>; query: string; loading: boolean }) {
  if (loading) return <Skeleton />;
  if (page === "Dashboard") return <Dashboard dashboard={dashboard} />;
  if (page === "Activity Timeline") return <Timeline query={query} />;
  if (page === "Advanced Filters") return <AdvancedFilters />;
  if (page === "Saved Filters") return <SavedFilters />;
  if (page === "Diff Explorer") return <DiffExplorer />;
  if (page === "Risk Center") return <RiskCenter />;
  if (page === "Alerts") return <Alerts />;
  if (page === "Exports") return <Exports />;
  if (page === "Coverage Matrix") return <CoverageMatrix />;
  if (page === "Sources & Sync") return <SourcesSync />;
  if (page === "Retention & Usage") return <Usage />;
  if (page === "Settings") return <SettingsPage />;
  if (page === "Billing") return <Billing />;
  return <Logs />;
}

function Dashboard({ dashboard }: { dashboard: Record<string, any> }) {
  const cards = dashboard.cards || {};
  const cardItems = [
    ["Today's total events", cards.totalEventsToday ?? 0],
    ["Risky changes today", cards.riskyChangesToday ?? 0],
    ["Product changes", cards.productChanges ?? 0],
    ["Order changes", cards.orderChanges ?? 0],
    ["Inventory changes", cards.inventoryChanges ?? 0],
    ["Discount changes", cards.discountChanges ?? 0],
    ["Theme changes", cards.themeChanges ?? 0],
    ["Content changes", cards.contentChanges ?? 0]
  ];
  return (
    <div className="page-stack">
      <section className="transparency">
        <ShieldCheck size={20} />
        <p>{dashboard.transparency}</p>
      </section>
      <section className="metric-grid">
        {cardItems.map(([label, value]) => (
          <article className="metric" key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </article>
        ))}
      </section>
      <section className="split">
        <Panel title="Recent changes timeline">
          <EventRows rows={dashboard.recent || []} />
        </Panel>
        <Panel title="Sync and webhook health">
          <SyncRows rows={dashboard.sync || []} />
        </Panel>
      </section>
      <Panel title="Coverage matrix summary">
        <CoverageRows rows={dashboard.coverageSummary || []} />
      </Panel>
    </div>
  );
}

function Timeline({ query }: { query: string }) {
  const [state, setState] = useState<ApiState<{ rows: any[] }>>({ loading: true });
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setState({ loading: true });
      api<{ rows: any[] }>(`/api/timeline?limit=30&q=${encodeURIComponent(query)}`)
        .then((data) => setState({ loading: false, data }))
        .catch((error) => setState({ loading: false, error: error.message }));
    }, 120);
    return () => window.clearTimeout(timeout);
  }, [query]);
  return (
    <Panel title="Server-side paginated activity">
      <div className="quick-filters">
        {["Price changed", "Price became 0", "Deleted items", "Theme published", "Inventory zero", "App actions", "Staff actions", "Order cancelled/refunded"].map((label) => (
          <button key={label}>{label}</button>
        ))}
      </div>
      {state.loading ? <Skeleton compact /> : <EventRows rows={state.data?.rows || []} />}
    </Panel>
  );
}

function AdvancedFilters() {
  const [filter, setFilter] = useState({ resourceType: "product", risk: "high", field: "price", destructive: false, salesRisk: true });
  const save = async () => api("/api/filters", { method: "POST", body: JSON.stringify({ name: `${filter.field} ${filter.risk}`, filter }) });
  return (
    <Panel title="Advanced filter builder">
      <div className="form-grid">
        {Object.entries(filter).map(([key, value]) => (
          <label key={key}>
            <span>{key}</span>
            {typeof value === "boolean" ? (
              <input type="checkbox" checked={value} onChange={(event) => setFilter((old) => ({ ...old, [key]: event.target.checked }))} />
            ) : (
              <input value={String(value)} onChange={(event) => setFilter((old) => ({ ...old, [key]: event.target.value }))} />
            )}
          </label>
        ))}
      </div>
      <div className="button-row">
        <button onClick={() => void save()}>Save filter</button>
        <button onClick={() => void save()}>Create alert from filter</button>
        <button onClick={() => void save()}>Schedule export</button>
      </div>
    </Panel>
  );
}

function SavedFilters() {
  const [state, setState] = useState<ApiState<{ filters: any[] }>>({ loading: true });
  useEffect(() => {
    api<{ filters: any[] }>("/api/filters").then((data) => setState({ loading: false, data })).catch((error) => setState({ loading: false, error: error.message }));
  }, []);
  return (
    <Panel title="Saved filters">
      <SimpleRows rows={state.data?.filters || []} empty="No saved filters yet." />
    </Panel>
  );
}

function DiffExplorer() {
  return (
    <Panel title="Before / after diff explorer">
      <div className="diff-preview">
        <div><span>Before</span><pre>Select an event to inspect its normalized snapshot.</pre></div>
        <div><span>After</span><pre>Field-level diffs appear here with compact summaries.</pre></div>
      </div>
    </Panel>
  );
}

function RiskCenter() {
  const [state, setState] = useState<ApiState<any>>({ loading: true });
  useEffect(() => {
    api("/api/risk").then((data) => setState({ loading: false, data })).catch((error) => setState({ loading: false, error: error.message }));
  }, []);
  return <Panel title="Risk Center"><SimpleRows rows={state.data?.critical || []} empty="No high or critical events captured yet." /></Panel>;
}

function Alerts() {
  const [result, setResult] = useState("");
  const createRule = async () => {
    const created = await api<{ id: string }>("/api/alerts/rules", { method: "POST", body: JSON.stringify({ name: "Risk level >= high", minRiskLevel: "high", channel: { type: "email" } }) });
    const tested = await api<{ delivery: { status: string; errorMessage?: string } }>(`/api/alerts/rules/${created.id}/test`, { method: "POST" });
    setResult(`${tested.delivery.status}${tested.delivery.errorMessage ? `: ${tested.delivery.errorMessage}` : ""}`);
  };
  return (
    <Panel title="Alerts">
      <p className="muted">Email, Slack, webhook, and daily summary channels are available. If a provider is not connected, LogFilters records the exact blocker instead of fake success.</p>
      <button onClick={() => void createRule()}>Create and test high-risk alert</button>
      {result ? <p className="notice">{result}</p> : null}
    </Panel>
  );
}

function Exports() {
  const [status, setStatus] = useState("");
  const run = async (type: "csv" | "excel" | "pdf") => {
    const result = await api<{ id: string; rowCount: number }>("/api/exports", { method: "POST", body: JSON.stringify({ type }) });
    setStatus(`${type.toUpperCase()} export completed with ${result.rowCount} rows.`);
  };
  return (
    <Panel title="Exports">
      <div className="button-row">
        <button onClick={() => void run("csv")}>Create CSV</button>
        <button onClick={() => void run("excel")}>Create Excel</button>
        <button onClick={() => void run("pdf")}>Create PDF</button>
      </div>
      {status ? <p className="notice">{status}</p> : null}
    </Panel>
  );
}

function CoverageMatrix() {
  const [state, setState] = useState<ApiState<{ coverage: any[]; note: string }>>({ loading: true });
  useEffect(() => {
    api<{ coverage: any[]; note: string }>("/api/coverage").then((data) => setState({ loading: false, data })).catch((error) => setState({ loading: false, error: error.message }));
  }, []);
  return (
    <Panel title="What LogFilters can and cannot see">
      <p className="muted">{state.data?.note}</p>
      <CoverageRows rows={state.data?.coverage || []} />
    </Panel>
  );
}

function SourcesSync() {
  const [status, setStatus] = useState("");
  const run = async (path: string) => {
    const result = await api<{ id: string; status: string }>(path, { method: "POST" });
    setStatus(`${result.status}: ${result.id}`);
  };
  return (
    <Panel title="Sources & Sync">
      <div className="button-row">
        <button onClick={() => void run("/api/sync/baseline")}>Run baseline</button>
        <button onClick={() => void run("/api/sync/event-poller")}>Run Event API poller</button>
        <button onClick={() => void run("/api/sync/reconcile")}>Run snapshot reconciliation</button>
      </div>
      {status ? <p className="notice">{status}</p> : null}
    </Panel>
  );
}

function Usage() {
  const [state, setState] = useState<ApiState<any>>({ loading: true });
  useEffect(() => {
    api("/api/usage").then((data) => setState({ loading: false, data })).catch((error) => setState({ loading: false, error: error.message }));
  }, []);
  return <Panel title="Retention & Usage"><SimpleRows rows={state.data?.usage || []} empty="Usage counters start after events, alerts, and exports are captured." /></Panel>;
}

function SettingsPage() {
  return (
    <Panel title="Settings">
      <div className="form-grid">
        {["Products", "Inventory", "Orders", "Discounts", "Themes", "Content"].map((label) => (
          <label key={label} className="checkbox-row"><input type="checkbox" checked readOnly /> {label}</label>
        ))}
        <label className="checkbox-row"><input type="checkbox" readOnly /> Customers requires PCD approval</label>
      </div>
    </Panel>
  );
}

function Billing() {
  const [state, setState] = useState<ApiState<any>>({ loading: true });
  useEffect(() => {
    api("/api/billing").then((data) => setState({ loading: false, data })).catch((error) => setState({ loading: false, error: error.message }));
  }, []);
  return (
    <Panel title="Billing">
      <div className="pricing-grid">
        {(state.data?.plans || []).map((plan: any) => (
          <article key={plan.handle}>
            <h3>{plan.name}</h3>
            <strong>{plan.price}</strong>
            <p>{plan.retention} day retention</p>
          </article>
        ))}
      </div>
    </Panel>
  );
}

function Logs() {
  const [state, setState] = useState<ApiState<any>>({ loading: true });
  useEffect(() => {
    api("/api/logs").then((data) => setState({ loading: false, data })).catch((error) => setState({ loading: false, error: error.message }));
  }, []);
  return <Panel title="Webhook and app logs"><SimpleRows rows={state.data?.logs || []} empty="No webhook logs captured yet." /></Panel>;
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="panel">
      <div className="panel-title"><h2>{title}</h2><SlidersHorizontal size={17} /></div>
      {children}
    </section>
  );
}

function EventRows({ rows }: { rows: any[] }) {
  if (!rows.length) return <p className="empty-inline">No captured audit events yet. Run a baseline sync, then make a covered test change in Shopify.</p>;
  return (
    <div className="event-table">
      {rows.map((row) => (
        <article key={row.id}>
          <span className={`risk ${row.risk_level}`}>{row.risk_level}</span>
          <div>
            <strong>{row.resource_name || row.resource_type}</strong>
            <p>{row.message}</p>
          </div>
          <small>{row.action}</small>
        </article>
      ))}
    </div>
  );
}

function SyncRows({ rows }: { rows: any[] }) {
  return <SimpleRows rows={rows} empty="No sync jobs have run yet." />;
}

function CoverageRows({ rows }: { rows: any[] }) {
  if (!rows.length) return <p className="empty-inline">Coverage is loading.</p>;
  return (
    <div className="coverage-table">
      {rows.map((row) => (
        <article key={row.resource}>
          <strong>{row.resource}</strong>
          <span className={`coverage ${String(row.status).replaceAll(" ", "-")}`}>{row.status}</span>
          <p>{row.message}</p>
        </article>
      ))}
    </div>
  );
}

function SimpleRows({ rows, empty }: { rows: any[]; empty: string }) {
  if (!rows.length) return <p className="empty-inline">{empty}</p>;
  return (
    <div className="simple-list">
      {rows.map((row, index) => (
        <article key={row.id || index}>
          <strong>{row.name || row.type || row.topic || row.resource_name || row.id}</strong>
          <span>{row.status || row.risk_level || row.created_at || ""}</span>
        </article>
      ))}
    </div>
  );
}

function Skeleton({ compact = false }: { compact?: boolean }) {
  return <div className={compact ? "skeleton compact" : "skeleton"}><Sparkles size={18} /> Loading LogFilters Guard data...</div>;
}

createRoot(document.getElementById("root") as HTMLElement).render(<App />);
